import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Gauge, Clock, ExternalLink, TrendingDown, TrendingUp, AlertTriangle, BarChart3, DollarSign, X } from 'lucide-react';
import { motoService } from '../services/api';
import { useDolarBlue } from '../hooks/useDolarBlue';
import { calcNormalizedChange } from '../utils/priceUtils';
import PriceChart from './PriceChart';
import CarThumbnail from './CarThumbnail';
import SearchAndFilters from './SearchAndFilters';
import SubscribeButton from './SubscribeButton';
import StockChart from './StockChart';
import PriceBoxplot from './PriceBoxplot';
import * as d3 from 'd3';

const PriceByYearChart = ({ data }) => {
  const ref = useRef();
  useEffect(() => {
    if (!data || !ref.current) return;
    const container = ref.current;
    container.innerHTML = '';
    const w = container.clientWidth, h = 300;
    const m = { top: 20, right: 16, bottom: 32, left: 50 };
    const iw = w - m.left - m.right, ih = h - m.top - m.bottom;
    const svg = d3.select(container).append('svg').attr('width', w).attr('height', h);
    const g = svg.append('g').attr('transform', `translate(${m.left},${m.top})`);
    const x = d3.scaleBand().domain(data.map(d => d.year)).range([0, iw]).padding(0.3);
    const y = d3.scaleLinear().domain([0, d3.max(data, d => d.avg_price)]).nice().range([ih, 0]);
    // Y axis
    g.append('g').call(d3.axisLeft(y).ticks(5).tickFormat(v => `$${(v/1000).toFixed(0)}k`))
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick line').attr('x2', iw).attr('stroke', '#4b5563').attr('stroke-opacity', 0.3))
      .call(g => g.selectAll('.tick text').attr('fill', '#9ca3af').style('font-size', '11px').style('font-feature-settings', '"tnum"'));
    // X axis
    g.append('g').attr('transform', `translate(0,${ih})`).call(d3.axisBottom(x).tickSizeOuter(0))
      .call(g => g.select('.domain').attr('stroke', '#4b5563'))
      .call(g => g.selectAll('.tick text').attr('fill', '#9ca3af').style('font-size', '10px'));
    // Bars
    g.selectAll('.bar').data(data).join('rect').attr('class', 'bar')
      .attr('x', d => x(d.year)).attr('y', d => y(d.avg_price))
      .attr('width', x.bandwidth()).attr('height', d => ih - y(d.avg_price))
      .attr('fill', '#2f6b4f').attr('opacity', 0.7).attr('rx', 2);
    // Direct labels on top
    g.selectAll('.val').data(data).join('text').attr('class', 'val')
      .attr('x', d => x(d.year) + x.bandwidth() / 2).attr('y', d => y(d.avg_price) - 6)
      .attr('text-anchor', 'middle').attr('fill', '#d1d5db').style('font-size', '9px')
      .style('font-feature-settings', '"tnum"')
      .text(d => `$${(d.avg_price/1000).toFixed(0)}k`);
  }, [data]);

  return (
    <div className="glass-effect rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Precio Medio por Año de Fabricación</h3>
      <div ref={ref} style={{ width: '100%', height: '300px' }} />
    </div>
  );
};

const MotoMarket = ({ showStats: showStatsProp, onToggleStats }) => {
  const [motos, setMotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queries, setQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('price_change');
  const [sortOrder, setSortOrder] = useState('desc');
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [kmsRange, setKmsRange] = useState([0, 200000]);
  const [showFilters, setShowFilters] = useState(false);
  const [yearRange, setYearRange] = useState([2015, new Date().getFullYear()]);
  const [years, setYears] = useState([]);
  const [condition, setCondition] = useState('all'); // 'all', '0km', 'used'
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [priceStats, setPriceStats] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [priceByYear, setPriceByYear] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const { convertArsToUsd, cotizacion } = useDolarBlue();
  const observer = useRef();

  const lastMotoRef = useCallback(node => {
    if (loading || isLoadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, isLoadingMore, hasMore]);

  // Cargar queries y seleccionar la primera por defecto
  useEffect(() => {
    motoService.getQueries().then(data => {
      setQueries(data);
      if (data.length > 0 && !selectedQuery) {
        setSelectedQuery(data[0]);
      }
    }).catch(console.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedQuery) {
      setPage(1);
      setMotos([]);
      loadMotos(1, true);
      loadYears();
      if (showStatsProp) loadStats();
    }
  }, [selectedQuery, sortBy, sortOrder, searchTerm, condition]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recargar cuando cambian filtros avanzados
  useEffect(() => {
    if (selectedQuery) {
      setPage(1);
      setMotos([]);
      loadMotos(1, true);
    }
  }, [priceRange, kmsRange, yearRange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page > 1 && selectedQuery) {
      loadMotos(page, false);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showStatsProp && selectedQuery) loadStats();
  }, [showStatsProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadYears = async () => {
    try {
      const data = await motoService.getYears(selectedQuery);
      setYears(data);
      if (data.length > 0) {
        setYearRange([Math.min(...data), Math.max(...data)]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadMotos = async (pageNum, isReset) => {
    if (isReset) setLoading(true);
    else setIsLoadingMore(true);
    setError(null);
    try {
      // Si es despublicadas, usar endpoint diferente
      if (condition === 'unpublished') {
        const data = await motoService.getUnpublished(selectedQuery);
        const newMotos = data.cars || [];
        setMotos(newMotos);
        setTotal(data.total || newMotos.length);
        setHasMore(false);
        setLoading(false);
        setIsLoadingMore(false);
        return;
      }

      const filters = {
        query: selectedQuery, sort_by: sortBy, sort_order: sortOrder,
        page: pageNum, per_page: 20
      };
      if (searchTerm) filters.search = searchTerm;
      if (priceRange[0] > 0) filters.min_price = priceRange[0];
      if (priceRange[1] < 50000) filters.max_price = priceRange[1];
      if (condition === '0km') { filters.max_kms = 0; }
      else if (condition === 'used') { filters.min_kms = 1; }
      else {
        if (kmsRange[0] > 0) filters.min_kms = kmsRange[0];
        if (kmsRange[1] < 200000) filters.max_kms = kmsRange[1];
      }
      if (yearRange[0] > 1990) filters.min_year = yearRange[0];
      if (yearRange[1] < new Date().getFullYear()) filters.max_year = yearRange[1];

      const data = await motoService.getAllMotos(filters);
      const newMotos = data.cars || data;
      if (isReset) setMotos(newMotos);
      else setMotos(prev => [...prev, ...newMotos]);
      setTotal(data.pagination?.total || newMotos.length);
      setHasMore(data.pagination?.has_next || false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      // Load boxplot price stats
      try {
        const priceRes = await fetch(`${apiUrl}/motos/stats/price-range?query=${selectedQuery}`);
        if (priceRes.ok) setPriceStats(await priceRes.json());
        else setPriceStats(null);
      } catch { setPriceStats(null); }
      // Load stock history
      try {
        const stockRes = await fetch(`${apiUrl}/motos/stats/stock-history?query=${selectedQuery}`);
        if (stockRes.ok) setStockData(await stockRes.json());
        else setStockData(null);
      } catch { setStockData(null); }
      // Load price by year
      try {
        const pbyRes = await fetch(`${apiUrl}/motos/stats/price-by-year?query=${selectedQuery}`);
        if (pbyRes.ok) setPriceByYear(await pbyRes.json());
        else setPriceByYear(null);
      } catch { setPriceByYear(null); }
    } catch (err) {
      console.error('Error loading moto stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    const num = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(price));
    return currency === 'ARS' ? `$ ${num}` : `US$ ${num}`;
  };

  const formatKms = (kms) => kms ? new Intl.NumberFormat('es-AR').format(kms) + ' km' : 'N/A';

  const formatDaysOld = (days) => {
    if (!days) return 'Nuevo';
    if (days < 30) return `${days} días`;
    if (days < 365) return `${Math.floor(days / 30)} meses`;
    return `${Math.floor(days / 365)} años`;
  };

  return (
    <div>
      {/* Filtros - misma experiencia que autos */}
      <SearchAndFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        kmsRange={kmsRange}
        setKmsRange={setKmsRange}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        selectedQuery={selectedQuery}
        setSelectedQuery={setSelectedQuery}
        queries={queries}
        yearRange={yearRange}
        setYearRange={setYearRange}
        years={years}
      />

      {/* Filtro condición + contador */}
      <div className="flex items-center justify-between mb-6 gap-2">
        <div className="flex items-center bg-[#1c1c1a] border border-gray-700 rounded-lg overflow-x-auto no-scrollbar">
          {[['all', 'Todas'], ['used', 'Usadas'], ['0km', '0 Km'], ['unpublished', 'Desp.']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setCondition(val)}
              className={`px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                condition === val ? 'bg-[#2f6b4f] text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {total > 0 && (
          <span className="text-sm text-gray-400">{total} motos encontradas</span>
        )}
      </div>

      {/* Panel de estadísticas */}
      {/* Panel de estadísticas */}
      {showStatsProp && (
        <div className="mb-8 animate-slide-up">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-6 h-6 text-[#48a877]" />
              <h2 className="text-xl font-bold text-white">Estadísticas de Mercado</h2>
              <span className="text-sm text-gray-400 uppercase">{selectedQuery?.replace(/-/g, ' ')}</span>
            </div>
            <button onClick={onToggleStats} className="text-gray-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {statsLoading && (
            <div className="glass-effect rounded-xl p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Cargando estadísticas...</p>
            </div>
          )}

          {priceStats && !statsLoading && (
            <div className="space-y-6">
              {/* Primera fila: Boxplot y KPIs - mismo layout que autos */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <PriceBoxplot stats={priceStats} />
                </div>
                <div className="space-y-4">
                  <div className="glass-effect rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <BarChart3 className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Total Motos</p>
                        <p className="text-2xl font-bold text-white">{priceStats.count.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  <div className="glass-effect rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-green-500/20 rounded-lg">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Precio Promedio</p>
                        <p className="text-xl font-bold text-green-400">
                          {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(priceStats.mean)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="glass-effect rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-purple-500/20 rounded-lg">
                        <TrendingUp className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Desviación Estándar</p>
                        <p className="text-lg font-bold text-purple-400">
                          {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(priceStats.std)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock Chart */}
              {stockData && (
                <StockChart stockData={stockData} />
              )}

              {/* Price by Year Chart */}
              {priceByYear && priceByYear.length > 0 && (
                <PriceByYearChart data={priceByYear} />
              )}
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
        </div>
      )}

      {/* Error */}
      {error && <div className="text-center py-10 text-red-400">{error}</div>}

      {/* Grid de motos */}
      {!loading && motos.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-6">
          {motos.map((moto, index) => (
            <div
              key={moto.id + '-' + index}
              ref={index === motos.length - 1 ? lastMotoRef : null}
              className="group bg-[#1c1c1a] rounded-xl overflow-hidden border border-[#2a2926] hover:border-gray-600 transition-all duration-300"
            >
              {/* Imagen */}
              <div className="relative">
                <CarThumbnail imageUrl={moto.pictures} title={moto.title} className="rounded-t-xl" />
                {moto.is_outlier === 1 && (
                  <div className="absolute top-2 right-2 bg-yellow-500/90 text-black text-xs px-2 py-0.5 rounded-full font-medium flex items-center space-x-1">
                    <AlertTriangle className="w-3 h-3" />
                    <span>Outlier</span>
                  </div>
                )}
                {moto.posible_republication === 1 && (
                  <div className="absolute top-2 left-2 bg-orange-500/90 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                    Repub.
                  </div>
                )}
              </div>

              <div className="p-3 lg:p-5">
                <h3 className="text-xs lg:text-base font-bold text-white line-clamp-2 mb-1 leading-tight">{moto.title}</h3>
                <p className="text-xs text-gray-400 mb-2">{moto.city}{moto.state ? `, ${moto.state}` : ''}</p>

                {/* Precio */}
                <div className="mb-3">
                  <span className="text-base lg:text-2xl font-bold text-green-400">
                    {formatPrice(moto.price, moto.currency_id)}
                  </span>
                  {moto.currency_id === 'ARS' && convertArsToUsd(moto.price) && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      ≈ US$ {new Intl.NumberFormat('es-AR').format(convertArsToUsd(moto.price))}
                      <span className="text-blue-400 ml-1">Blue</span>
                    </p>
                  )}
                  {(() => {
                    const pct = calcNormalizedChange(moto, cotizacion);
                    return pct != null && pct !== 0 && (
                      <div className={`flex items-center space-x-1 mt-1 ${pct < 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {pct < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                        <span className="text-xs font-medium">{Math.abs(pct).toFixed(1)}%</span>
                      </div>
                    );
                  })()}
                </div>

                {/* Price chart */}
                {moto.price_history && moto.price_history.length > 1 && (
                  <div className="mb-3">
                    <PriceChart priceHistory={moto.price_history} currentPrice={moto.price} currency={moto.currency_id} />
                  </div>
                )}

                {/* Info */}
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-400 mb-3">
                  <div className="flex items-center space-x-1">
                    <Gauge className="w-4 h-4" />
                    <span>{formatKms(moto.kilometers)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{formatDaysOld(moto.days_old || moto.days_published)}</span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {moto.vehicle_year && (
                    <span className="px-2 py-0.5 bg-[#232321]/30 rounded text-xs text-gray-300">{moto.vehicle_year}</span>
                  )}
                  {moto.engine_displacement && moto.engine_displacement.toLowerCase() !== 'na' && (
                    <span className="px-2 py-0.5 bg-[#7b8fa8]/10 rounded text-xs text-[#7b8fa8]">{moto.engine_displacement}</span>
                  )}
                  {moto.version_normalizada && moto.version_normalizada.toLowerCase() !== 'na' && (
                    <span className="px-2 py-0.5 bg-[#48a877]/15 rounded text-xs text-[#48a877] truncate max-w-[140px]">{moto.version_normalizada}</span>
                  )}
                </div>

                {/* Link */}
                {/* Seguimiento + Link */}
                <div className="flex items-center gap-2">
                  <SubscribeButton vehicleId={moto.id} vehicleType="moto" />
                  <a
                    href={moto.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center space-x-1 py-2 bg-[#7b8fa8]/10 hover:bg-[#7b8fa8]/10 border border-[#2a2926] rounded-lg text-[#7b8fa8] text-sm transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Ver en ML</span>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading more */}
      {isLoadingMore && (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-400"></div>
        </div>
      )}
    </div>
  );
};

export default MotoMarket;
